// Module 27's sidebar contribution (contract 27 §11, v3 `components/Layouts/DefaultLayout.vue`).
//
// ══ WHAT THIS FILE DOES *NOT* CONTAIN, AND WHY ══════════════════════════════════════════════
// ORP-18947 asks for "the 5 nav branches, 11 entries". Reproducing them would render the sidebar
// TWICE: module 08's `src/features/reports/nav.ts` already ships the shell GROUPS — Creative
// Analytics, Budget & Performance, Ecommerce and Travel — each with its
// visibility rule and with the leaves whose pages 08 also owns (`creativeDashboard`,
// `budgetAllocation`, `productPerformance`, `hotelAds`, `appAcquisition`, `storeAnalysis`,
// plus `rfmClustering` under Audience, which is module 24's). The shell composes contributions by
// CONCATENATION (`app/navigation/navContributions.ts`) — there is no merge-by-navKey — so a second
// "Budget & Performance" group would be a second group, not a merged one.
//
// Module 27 owns the leaves 08 originally deferred. Creative Watchlist now lives directly in 08's
// Creative Analytics group; route-ready Performance Overview, Benchmark, Customer Intelligence and
// Product Insights are composed from this array into their 08 groups by the shell. Apple Search Ads
// stays deep-link-only. `commerceNavItems.spec.ts` pins the no-duplicate-navKey
// invariant against 08's array so the double-render cannot reappear.
//
// ══ REGISTRATION STATE ══════════════════════════════════════════════════════════════════════
// `app/navigation/registerShellNavigation.ts` composes leaves from this array INTO 08's groups
// (contributions concatenate; a standalone leaf would render top-level), and only leaves whose
// routes exist may compose: `NavTreeItem.vue` renders `to` through an unguarded `<RouterLink>` —
// an unknown route name throws during render.
// - `performanceOverview` and App Acquisition are ordered under Budget & Performance.
// - `benchmark`, `customerIntelligence` and `productInsights` are composed into their live groups.
// - `AppleSearchAds` resolves but stays DEEP-LINK ONLY by product constraint: v3's entire App
//   Analytics nav branch is commented out on live main (`DefaultLayout.vue:481–501`) and product
//   has not decided whether v4 revives it, so its leaf must not become visible.
//
// `meta` carries v3's `search_value`/`importance` search-index payload verbatim (the ratified home
// for it, `app/navigation/types.ts:73–78`). It is machine metadata, not UI copy, and stays
// untranslated pending module 15 (contract §13, open question 10).

export const commerceNavItems = [
  {
    // v3 `DefaultLayout.vue:311–319`, under "Budget & Performance". `hidePermission` is carried on
    // the LEAF although v3 carries it on the parent branch: the branch hides everything beneath
    // it, so `report[6] ∧ ¬hide_report_6` is this entry's effective gate wherever it is composed.
    // labelKey === the route's `titleKey` (contract §11): the sidebar row and the page header
    // must never drift apart. Same reason 08 pins `appAcquisition` explicitly.
    labelKey: 'commerceAnalytics.performanceOverview.title',
    navKey: 'performanceOverview',
    to: { name: 'PerformanceOverview' },
    hidePermission: 'hide_report_6',
    visibility: { control: 'report' as const, id: [6] },
    meta: {
      importance: 8,
      searchValue:
        'Performance Overview section under Performance Overview analysis. Get a high-level view across all marketing and measurement platforms like GA, BQ, Adjust. Benchmark performance and spot issues. a across adjust all analysis and benchmark bq ga get high-level issues like marketing measurement overview performance platforms section spot under view Campaign Breakdown Ad Group Breakdown Ad Breakdown Key Events Campaign Ad Group Ad',
    },
  },
  {
    // v3 `DefaultLayout.vue:452`: unnamed report-67 leaf under Ecommerce. The parent
    // `reportEcommerce` rule supplies the `workspace.is_ecommerce` half of the gate.
    labelKey: 'commerceAnalytics.customerIntelligence.title',
    navKey: 'customerIntelligence',
    to: { name: 'CustomerIntelligence' },
    visibility: { control: 'report' as const, id: [67] },
    meta: {
      importance: 5,
      searchValue:
        'Customer Intelligence ecommerce customer overview total customers active subscribers new customers returning customer rate RFM segment audience size',
    },
  },
  {
    // v3 `:452–462`, under "Ecommerce". v3 wraps the rule in a `Multiple` of ONE
    // (`[{control:'ProductInsights', id:['merchant_center']}]`) — the wrapper is noise and the
    // integration id is already inside the predicate, so the bare control is used.
    // The parent's `Report Ecommerce` gate (which is where `workspace.is_ecommerce` comes from) is
    // NOT expressible on a leaf — `multiple` is OR, not AND — so this entry MUST be composed into
    // 08's `ecommerceGroup` rather than contributed standalone.
    labelKey: 'commerceAnalytics.productInsights.title',
    navKey: 'productInsights',
    to: { name: 'ProductInsights' },
    visibility: { control: 'productInsights' as const },
    meta: {
      importance: 7,
      searchValue:
        'Product Insights section under Product Insights analysis. Gain competitive product insights using Google Merchant Center data. Spot trends, pricing gaps, and opportunities. analysis and center competitive data gain gaps google insights merchant opportunities pricing product section spot trends under using Products with Issues Best Seller Price Sensitivity Analysis Top Competitors Demand Trend Products with Issues Best Seller Price Sensitivity Analysis Top Competitors Products with Issues Catalog Issue Types Demand Increased Products Price Sensitive Products',
    },
  },
  {
    // Under the COMMENTED-OUT App Analytics branch. Kept for deep links, never registered.
    labelKey: 'commerceAnalytics.appleSearchAds.title',
    navKey: 'appleSearchAds',
    to: { name: 'AppleSearchAds' },
    visibility: { control: 'report' as const, id: [58] },
    meta: {
      importance: 6,
      searchValue:
        'Apple Search Ads section under Apple Search Ads analysis. Track Apple Search Ads performance using App Store and App Store Connect. Optimize keyword bids and improve visibility. ads analysis and app apple bids connect improve keyword optimize performance search section store track under using visibility Keyword Search Term Performance Trendline Top Performers Performance Segments Performance Breakdown',
    },
  },
  {
    // v3 `:287–296` — the one leaf in the module gated on a schedule tab rather than a report.
    labelKey: 'commerceAnalytics.benchmark.title',
    navKey: 'benchmark',
    to: { name: 'Benchmark' },
    visibility: { control: 'scheduleTab' as const, id: 66, tabs: ['tiktok_ads_ad'] },
    meta: {
      importance: 9,
      searchValue:
        'Overview section under Creative Performance analysis. Evaluate creative impact across Google, Meta, TikTok using measurement tools (GA, BQ, Adjust). Optimize top-performing visuals and texts. (ga across adjust) analysis and bq creative evaluate google impact measurement meta optimize overview performance section texts tiktok tools top-performing under using visuals Image Image Performance Video Competition Landscape Video Performance Text Text Performance Benchmark Ad Level Creative Level Performance Matrix',
    },
  },
]
